<template>
  <div class="email-settings p-4">
    <admin-nav />
    <h2 class="mb-4">
      <i class="fas fa-envelope-open-text me-2"></i>
      Email Settings
    </h2>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <a 
          class="nav-link" 
          :class="{ active: activeTab === 'settings' }"
          href="#"
          @click.prevent="activeTab = 'settings'"
        >
          <span class="badge" :class="activeTab === 'settings' ? 'tab-badge-active' : 'tab-badge'">
            <i class="fas fa-cog me-1"></i>
            Settings
          </span>
        </a>
      </li>
      <li class="nav-item">
        <a 
          class="nav-link" 
          :class="{ active: activeTab === 'logs' }"
          href="#"
          @click.prevent="activeTab = 'logs'; loadEmailLogs()"
        >
          <span class="badge" :class="activeTab === 'logs' ? 'tab-badge-active' : 'tab-badge'">
            <i class="fas fa-list me-1"></i>
            Email Log
          </span>
        </a>
      </li>
    </ul>

    <!-- Settings Tab -->
    <div v-show="activeTab === 'settings'">
      <!-- Email Provider Selection -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fas fa-mail-bulk me-2"></i>
            Email Provider
          </h5>
        </div>
        <div class="card-body">
          <div class="d-flex gap-4">
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="emailProvider"
                id="providerSmtp"
                value="smtp"
                v-model="emailProvider"
              />
              <label class="form-check-label" for="providerSmtp">
                <i class="fas fa-server me-1"></i>
                SMTP Server
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="emailProvider"
                id="providerGraph"
                value="graph"
                v-model="emailProvider"
              />
              <label class="form-check-label" for="providerGraph">
                <i class="fab fa-microsoft me-1"></i>
                Microsoft Graph
              </label>
            </div>
          </div>
          <small class="text-muted mt-2 d-block">
            Select which email provider to use for sending emails.
          </small>
        </div>
      </div>

      <!-- SMTP Configuration Card -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fas fa-server me-2"></i>
          SMTP Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">SMTP Host <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="settings.smtpHost"
                placeholder="smtp.example.com"
                required
              />
              <small class="text-muted">SMTP server hostname</small>
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">SMTP Port <span class="text-danger">*</span></label>
              <input
                type="number"
                class="form-control"
                v-model="settings.smtpPort"
                placeholder="587"
                required
              />
              <small class="text-muted">Usually 587 or 465</small>
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">Use SSL</label>
              <select class="form-select" v-model="settings.useSsl">
                <option :value="false">No</option>
                <option :value="true">Yes</option>
              </select>
              <small class="text-muted">Enable SSL encryption</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Username <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="settings.smtpUsername"
                placeholder="user@example.com"
                required
              />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Password <span class="text-danger">*</span></label>
              <input
                type="password"
                class="form-control"
                v-model="settings.smtpPassword"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">From Email <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                v-model="settings.fromEmail"
                placeholder="noreply@example.com"
                required
              />
              <small class="text-muted">Default sender email address</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">From Name</label>
              <input
                type="text"
                class="form-control"
                v-model="settings.fromName"
                placeholder="Your Company Name"
              />
              <small class="text-muted">Display name for sender</small>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <i class="fas fa-save me-2"></i>
              {{ saving ? 'Saving...' : 'Save Settings' }}
            </button>
            <button type="button" class="btn btn-outline-secondary" @click="loadSettings">
              <i class="fas fa-sync-alt me-2"></i>
              Reload
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Microsoft Graph Configuration Card -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fab fa-microsoft me-2"></i>
          Microsoft Graph Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveGraphSettings">
          <div class="alert alert-info mb-3">
            <i class="fas fa-info-circle me-2"></i>
            To use Microsoft Graph for sending emails, you need to register an application in Azure AD 
            and grant it the <code>Mail.Send</code> permission.
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Tenant ID <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.tenantId"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
              />
              <small class="text-muted">Azure AD Tenant ID (Directory ID)</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Client ID <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.clientId"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
              />
              <small class="text-muted">Application (client) ID</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Client Secret <span class="text-danger">*</span></label>
              <input
                type="password"
                class="form-control"
                v-model="graphSettings.clientSecret"
                placeholder="••••••••"
                required
              />
              <small class="text-muted">Application client secret</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">From Email <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                v-model="graphSettings.fromEmail"
                placeholder="noreply@yourdomain.com"
                required
              />
              <small class="text-muted">Sender email address (must be a valid mailbox)</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">From Name</label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.fromName"
                placeholder="Your Company Name"
              />
              <small class="text-muted">Display name for sender</small>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="savingGraph">
              <i class="fas fa-save me-2"></i>
              {{ savingGraph ? 'Saving...' : 'Save Settings' }}
            </button>
            <button type="button" class="btn btn-outline-secondary" @click="loadSettings">
              <i class="fas fa-sync-alt me-2"></i>
              Reload
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Test Email Card -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fas fa-vial me-2"></i>
          Send Test Email
        </h5>
      </div>
      <div class="card-body">
        <p class="text-muted mb-3">
          Send a test email to verify your email configuration is working correctly.
        </p>
        <form @submit.prevent="sendTestEmail">
          <div class="row">
            <div class="col-md-8 mb-3">
              <label class="form-label">Test Email Recipient <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                v-model="testEmail.recipient"
                placeholder="test@example.com"
                required
              />
            </div>
            <div class="col-md-4 mb-3 d-flex align-items-end">
              <button type="submit" class="btn btn-success w-100" :disabled="sendingTest">
                <i class="fas fa-paper-plane me-2"></i>
                {{ sendingTest ? 'Sending...' : 'Send Test Email' }}
              </button>
            </div>
          </div>
        </form>
        
        <!-- Test Result -->
        <div v-if="testResult" class="alert mt-3" :class="testResult.success ? 'alert-success' : 'alert-danger'">
          <i class="fas" :class="testResult.success ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
          {{ testResult.message }}
        </div>
      </div>
    </div>
    </div><!-- End Settings Tab -->

    <!-- Email Log Tab -->
    <div v-show="activeTab === 'logs'">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <i class="fas fa-list me-2"></i>
            Email Log
          </h5>
          <div class="d-flex gap-2">
            <select class="form-select form-select-sm" style="width: auto;" v-model="logFilter" @change="loadEmailLogs">
              <option value="">All Emails</option>
              <option value="Pending">Pending</option>
              <option value="Sent">Sent</option>
              <option value="Failed">Failed</option>
            </select>
            <button class="btn btn-sm btn-outline-secondary" @click="loadEmailLogs" :disabled="loadingLogs">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loadingLogs }"></i>
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="loadingLogs" class="text-center py-4">
            <i class="fas fa-spinner fa-spin me-2"></i> Loading email logs...
          </div>
          <div v-else-if="emailLogs.length === 0" class="text-center py-4 text-muted">
            <i class="fas fa-inbox me-2"></i> No emails found
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-striped mb-0">
              <thead class="table-light">
                <tr>
                  <th>Status</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Created</th>
                  <th>Sent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="email in emailLogs" :key="email.EmailLogId">
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(email.Status)">
                      {{ email.Status }}
                    </span>
                    <small v-if="email.RetryCount > 0" class="text-muted d-block">
                      Retries: {{ email.RetryCount }}
                    </small>
                  </td>
                  <td>
                    <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="email.ToAddresses">
                      {{ email.ToAddresses }}
                    </span>
                  </td>
                  <td>
                    <span class="text-truncate d-inline-block" style="max-width: 250px;" :title="email.Subject">
                      {{ email.Subject }}
                    </span>
                  </td>
                  <td>
                    <small>{{ formatDate(email.CreatedDate) }}</small>
                  </td>
                  <td>
                    <small v-if="email.SentDate">{{ formatDate(email.SentDate) }}</small>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <!-- View button -->
                      <button 
                        class="btn btn-outline-info"
                        @click="viewEmail(email)"
                        title="View Email"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                      <!-- Send Now button for Pending/Failed emails -->
                      <button 
                        v-if="email.Status === 'Pending' || email.Status === 'Failed'"
                        class="btn btn-outline-primary"
                        @click="sendEmailNow(email.EmailLogId)"
                        :disabled="sendingEmailId === email.EmailLogId"
                        title="Send Now"
                      >
                        <i class="fas" :class="sendingEmailId === email.EmailLogId ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                      </button>
                      <!-- Resend button for Sent emails -->
                      <button 
                        v-if="email.Status === 'Sent'"
                        class="btn btn-outline-secondary"
                        @click="resendEmail(email.EmailLogId)"
                        :disabled="resendingEmailId === email.EmailLogId"
                        title="Resend"
                      >
                        <i class="fas" :class="resendingEmailId === email.EmailLogId ? 'fa-spinner fa-spin' : 'fa-redo'"></i>
                      </button>
                    </div>
                    <!-- Error message tooltip -->
                    <i 
                      v-if="email.ErrorMessage" 
                      class="fas fa-exclamation-circle text-danger ms-2" 
                      :title="email.ErrorMessage"
                    ></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div><!-- End Email Log Tab -->

    <!-- View Email Modal -->
    <div class="modal" :class="{ 'show d-block': showViewModal }" v-if="showViewModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-envelope me-2"></i>
              Email Details
            </h5>
            <button type="button" class="btn-close" @click="showViewModal = false"></button>
          </div>
          <div class="modal-body" v-if="selectedEmail">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>Status:</strong><br>
                <span class="badge" :class="getStatusBadgeClass(selectedEmail.Status)">
                  {{ selectedEmail.Status }}
                </span>
                <span v-if="selectedEmail.RetryCount > 0" class="ms-2 text-muted">
                  ({{ selectedEmail.RetryCount }} retries)
                </span>
              </div>
              <div class="col-md-6">
                <strong>Created:</strong><br>
                {{ formatDate(selectedEmail.CreatedDate) }}
                <span v-if="selectedEmail.SentDate" class="ms-3">
                  <strong>Sent:</strong> {{ formatDate(selectedEmail.SentDate) }}
                </span>
              </div>
            </div>
            <div class="mb-3">
              <strong>To:</strong><br>
              {{ selectedEmail.ToAddresses }}
            </div>
            <div class="mb-3">
              <strong>Subject:</strong><br>
              {{ selectedEmail.Subject }}
            </div>
            <div class="mb-3">
              <strong>Body:</strong><br>
              <div class="border p-3 bg-light" style="max-height: 400px; overflow-y: auto;" v-html="selectedEmailBody"></div>
            </div>
            <div v-if="selectedEmail.ErrorMessage" class="mb-3">
              <strong>Error:</strong><br>
              <div class="alert alert-danger mb-0">{{ selectedEmail.ErrorMessage }}</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showViewModal = false">Close</button>
            <button 
              v-if="selectedEmail?.Status === 'Pending' || selectedEmail?.Status === 'Failed'"
              type="button" 
              class="btn btn-primary" 
              @click="sendEmailNow(selectedEmail.EmailLogId); showViewModal = false;"
            >
              <i class="fas fa-paper-plane me-2"></i>
              Send Now
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showViewModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import OptionsService from '@/services/OptionsService';
import Api, { EmailLogModel } from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import AdminNav from '@/components/Shared/AdminNav.vue';

// Tab state
const activeTab = ref<'settings' | 'logs'>('settings');

// Email provider selection
const emailProvider = ref<'smtp' | 'graph'>('smtp');

// SMTP settings
const settings = ref({
  smtpHost: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  useSsl: true,
  fromEmail: '',
  fromName: ''
});

// Microsoft Graph settings
const graphSettings = ref({
  tenantId: '',
  clientId: '',
  clientSecret: '',
  fromEmail: '',
  fromName: ''
});

const testEmail = ref({
  recipient: ''
});

const saving = ref(false);
const savingGraph = ref(false);
const loading = ref(false);
const sendingTest = ref(false);
const testResult = ref<{ success: boolean; message: string } | null>(null);

// Email log state
const emailLogs = ref<EmailLogModel[]>([]);
const loadingLogs = ref(false);
const logFilter = ref('');
const sendingEmailId = ref<string | null>(null);
const resendingEmailId = ref<string | null>(null);

// View email modal state
const showViewModal = ref(false);
const selectedEmail = ref<EmailLogModel | null>(null);
const selectedEmailBody = ref('');

// Flag to prevent watcher from triggering on initial load
const initialLoadComplete = ref(false);

const loadSettings = async () => {
  loading.value = true;
  try {
    // Load email provider selection
    emailProvider.value = await OptionsService.getEmailProvider();

    // Load SMTP settings
    const smtpSettings = await OptionsService.getSmtpSettings();
    settings.value = {
      smtpHost: smtpSettings.host,
      smtpPort: smtpSettings.port,
      smtpUsername: smtpSettings.username,
      smtpPassword: smtpSettings.password,
      useSsl: smtpSettings.enableSsl,
      fromEmail: smtpSettings.fromEmail,
      fromName: smtpSettings.fromName
    };

    // Load Microsoft Graph settings
    const graphData = await OptionsService.getGraphSettings();
    graphSettings.value = {
      tenantId: graphData.tenantId,
      clientId: graphData.clientId,
      clientSecret: graphData.clientSecret,
      fromEmail: graphData.fromEmail,
      fromName: graphData.fromName
    };
  } catch (error) {
    console.error('Error loading email settings:', error);
    Alert.notify({ text: 'Failed to load email settings', type: 'error' });
  } finally {
    loading.value = false;
    initialLoadComplete.value = true;
  }
};

const saveSettings = async () => {
  saving.value = true;
  try {
    await OptionsService.saveSmtpSettings({
      host: settings.value.smtpHost,
      port: settings.value.smtpPort,
      username: settings.value.smtpUsername,
      password: settings.value.smtpPassword,
      enableSsl: settings.value.useSsl,
      fromEmail: settings.value.fromEmail,
      fromName: settings.value.fromName
    });
    Toast.success('Settings saved successfully!');
  } catch (error: any) {
    console.error('Error saving email settings:', error);
    Alert.notify({ text: 'Failed to save settings: ' + (error.message || 'Unknown error'), type: 'error' });
  } finally {
    saving.value = false;
  }
};

const saveGraphSettings = async () => {
  savingGraph.value = true;
  try {
    await OptionsService.saveGraphSettings({
      tenantId: graphSettings.value.tenantId,
      clientId: graphSettings.value.clientId,
      clientSecret: graphSettings.value.clientSecret,
      fromEmail: graphSettings.value.fromEmail,
      fromName: graphSettings.value.fromName
    });
    Toast.success('Microsoft Graph settings saved successfully!');
  } catch (error: any) {
    console.error('Error saving Graph settings:', error);
    Alert.notify({ text: 'Failed to save settings: ' + (error.message || 'Unknown error'), type: 'error' });
  } finally {
    savingGraph.value = false;
  }
};

const sendTestEmail = async () => {
  if (!testEmail.value.recipient) {
    Alert.notify({ text: 'Please enter a recipient email address', type: 'warning' });
    return;
  }

  sendingTest.value = true;
  testResult.value = null;

  try {
    await Api.sendEmail({
      to: testEmail.value.recipient,
      subject: 'Test Email from Radar',
      body: 'This is a test email to verify your SMTP configuration is working correctly.'
    });
    
    testResult.value = {
      success: true,
      message: 'Test email queued successfully! It will be sent shortly.'
    };
  } catch (error: any) {
    console.error('Error sending test email:', error);
    testResult.value = {
      success: false,
      message: 'Failed to send test email: ' + (error.message || 'Unknown error')
    };
  } finally {
    sendingTest.value = false;
  }
};

// Email log functions
const loadEmailLogs = async () => {
  loadingLogs.value = true;
  try {
    emailLogs.value = await Api.getEmailLogs({
      status: logFilter.value || undefined,
      pageSize: 50
    });
  } catch (error) {
    console.error('Error loading email logs:', error);
    Alert.notify({ text: 'Failed to load email logs', type: 'error' });
  } finally {
    loadingLogs.value = false;
  }
};

const sendEmailNow = async (emailLogId: string) => {
  sendingEmailId.value = emailLogId;
  try {
    await Api.sendEmailNow(emailLogId);
    Toast.success('Email sent successfully');
    await loadEmailLogs();
  } catch (error: any) {
    console.error('Error sending email:', error);
    Alert.notify({ text: 'Failed to send email: ' + (error.message || 'Unknown error'), type: 'error' });
  } finally {
    sendingEmailId.value = null;
  }
};

const resendEmail = async (emailLogId: string) => {
  resendingEmailId.value = emailLogId;
  try {
    await Api.resendEmail(emailLogId);
    Toast.success('Email queued for resending');
    await loadEmailLogs();
  } catch (error: any) {
    console.error('Error resending email:', error);
    Alert.notify({ text: 'Failed to resend email: ' + (error.message || 'Unknown error'), type: 'error' });
  } finally {
    resendingEmailId.value = null;
  }
};

const viewEmail = (email: EmailLogModel) => {
  selectedEmail.value = email;
  // Parse the body from BodyJson
  if (email.BodyJson) {
    try {
      const bodyData = JSON.parse(email.BodyJson);
      // Check both PascalCase (from C#) and lowercase property names
      selectedEmailBody.value = bodyData.Html || bodyData.html || bodyData.Text || bodyData.text || '';
    } catch {
      selectedEmailBody.value = email.BodyJson;
    }
  } else {
    selectedEmailBody.value = '<em class="text-muted">No body content</em>';
  }
  showViewModal.value = true;
};

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'Pending': return 'bg-warning text-dark';
    case 'Sent': return 'bg-success';
    case 'Failed': return 'bg-danger';
    case 'Processing': return 'bg-info';
    default: return 'bg-secondary';
  }
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Watch for email provider changes and save automatically (only after initial load)
watch(emailProvider, async (newValue, oldValue) => {
  if (!initialLoadComplete.value) return; // Skip during initial load
  if (newValue !== oldValue) {
    try {
      await OptionsService.saveEmailProvider(newValue);
      Toast.success(`Email provider changed to ${newValue === 'smtp' ? 'SMTP' : 'Microsoft Graph'}`);
    } catch (error) {
      console.error('Error saving email provider:', error);
    }
  }
});

onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.email-settings {
  max-width: 1200px;
  margin: 0 auto;
}

/* Tab badge styling */
.tab-badge {
  background-color: #e9ecef;
  color: #495057;
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.tab-badge-active {
  background-color: #3B9796;
  color: #fff;
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.nav-link {
  border: none;
  background: transparent;
}

.nav-link:hover .tab-badge {
  background-color: #3B9796;
  color: #fff;
}
</style>
